import {useContext, useEffect, useMemo} from 'react';
import {Mortgage} from "./MortgageProvider";
import {useMortgageFigures} from "../../components/client/application-stages/_common/verified-figures/useMortgageFigures";
import useApplicationUploads from "./useApplicationUploads";
import useBackendUsers from "./useBackendUsers";
import boiDocumentChecklist from "../../../assets/scripts/lender-checklists/boi/document-checklist";
import {particulars} from "./particulars";
import {verifiedAccounts, verifiedInvestments} from "../../components/client/application-stages/_common/funding/verifiedAccounts";
import mortgageToLenderTestFigures, {
    mortgageToExpandedFigures
} from "../../components/client/application-stages/_common/lender-tests/v3/mortgageToLenderTestFigures";
import {flowRite} from "./MortgageCompletedFlowItems";
import useAdminMortgageDocuments from "./useAdminMortgageDocuments";
import useRates from "./useRates";
import useMortgageUploadSections from "../../components/client/_new/uploads-overview/useMortgageUploadSections";
import {bankStatementSummary} from "../../components/client/_new/savings-rate/BankingSavingsRate";
import {App} from "antd";


function useMortgage(props) {
    const app = App.useApp()
    const mortgage = useContext(Mortgage)
    const {me} = useBackendUsers()
    // get the last submission by createdAt
    const mortgageUploads = useApplicationUploads()
    const betterMortgage = {
        ...mortgage,
        mortgageUploads,
    }
    if (mortgage){
        betterMortgage.particulars = particulars(betterMortgage)
    }
    const checklists = {}
    if (betterMortgage.activeSubmission && betterMortgage.activeSubmission?.lenderCode) {
        switch( betterMortgage.activeSubmission?.lenderCode){
            case 'boi':
                checklists.SIGN = boiDocumentChecklist(betterMortgage)
                break
            default:

        }
    }
    const verifiedFigures = useMortgageFigures(betterMortgage, true)
    const unverifiedFigures = useMortgageFigures(betterMortgage, false)
    const {getProductsForMortgage, allRates} = useRates()

    const evenBetterMortgage = {
        ...betterMortgage,
        applicant1:{
            ...betterMortgage.applicant1,
            verifiedAccounts : verifiedAccounts(betterMortgage, betterMortgage.mortgageUploads.apply.filter(it => it.applicant === 1)),
            verifiedInvestments: verifiedInvestments(betterMortgage, betterMortgage.mortgageUploads.apply.filter(it => it.applicant === 1))
        },
        applicant2:{
            ...betterMortgage.applicant2,
            verifiedAccounts : verifiedAccounts(betterMortgage, betterMortgage.mortgageUploads.apply.filter(it => it.applicant === 2)),
            verifiedInvestments: verifiedInvestments(betterMortgage, betterMortgage.mortgageUploads.apply.filter(it => it.applicant === 2))
        },
        figures                   : {
            verified  : verifiedFigures,
            unverified: unverifiedFigures,
        },
        checklists,
        completedFlowItems        : {
            ...mortgage.completedFlowItems,
            getMarkCompleteUpdateMutation: (key, submissionID = null, newDate = null) => {
                if (submissionID) {
                    const submission = mortgage.submissions.find(a => a.id === submissionID)
                    let prevSubmissionItems = submission ? submission.completedFlowItems : {}
                    return {
                        target: `submissions[${submissionID}].completedFlowItems`,
                        value : JSON.stringify({
                            ...prevSubmissionItems,
                            [key]: newDate || new Date().toISOString()
                        })
                    }
                }
                return {
                    target: 'completedFlowItems',
                    value : JSON.stringify({
                        ...mortgage.completedFlowItems,
                        [key]: newDate || new Date().toISOString()
                    })
                }
            },
            getMarkCompleteUpdateMutationUndo: (key, submissionID = false) => {
                if (submissionID) {
                    const submission = mortgage.submissions.find(a => a.id === submissionID)
                    let prevSubmissionItems = submission ? {...submission.completedFlowItems} : {}
                    delete prevSubmissionItems[key]
                    return {
                        target: `submissions[${submissionID}].completedFlowItems`,
                        value : JSON.stringify(prevSubmissionItems)
                    }
                }
                const old = {...mortgage.completedFlowItems}
                delete old[key]
                return {
                    target: 'completedFlowItems',
                    value : JSON.stringify(old)
                }
            },
        },
        completedFlowItemsForAdmin: {
            ...mortgage.completedFlowItemsForAdmin,
            // the admin items can be faked
            getMarkCompleteUpdateMutation: (key, submissionID=false, newDate = null) => {
                let record = {
                    on: newDate || new Date().toISOString(),
                    by: me.sub
                }
                if (submissionID) {
                    const submission = mortgage.submissions.find(a => a.id === submissionID)
                    let prevSubmissionItems = submission ? submission.completedFlowItemsForAdmin : {}
                    return {
                        target: `submissions[${submissionID}].completedFlowItemsForAdmin`,
                        value : JSON.stringify({
                            ...prevSubmissionItems,
                            [key]: record
                        })
                    }
                }
                return {
                    target: 'completedFlowItemsForAdmin',
                    value : JSON.stringify({
                        ...mortgage.completedFlowItemsForAdmin,
                        [key]: record
                    })
                }
            },
            getMarkCompleteUpdateMutationUndo: (key, submissionID=false, newDate = null) => {
                if (submissionID) {
                    const submission = mortgage.submissions.find(a => a.id === submissionID)
                    let prevSubmissionItems = submission ? submission.completedFlowItemsForAdmin : {}
                    delete prevSubmissionItems[key]
                    return {
                        target: `submissions[${submissionID}].completedFlowItemsForAdmin`,
                        value : JSON.stringify(prevSubmissionItems)
                    }
                }
                const old = {...mortgage.completedFlowItemsForAdmin}
                delete old[key]
                return {
                    target: 'completedFlowItemsForAdmin',
                    value : JSON.stringify(old)
                }
            },
        },
    }
    evenBetterMortgage.mortgageAdminDocuments = useAdminMortgageDocuments(evenBetterMortgage)
    evenBetterMortgage.uploadSections = useMortgageUploadSections(evenBetterMortgage)

    evenBetterMortgage.lenderTestFigures = (verified = true, stage = 'APPLY') =>{
        return mortgageToExpandedFigures(evenBetterMortgage, verified, stage)
    }

    const verified = mortgageToLenderTestFigures(evenBetterMortgage, true)
    const unverified = mortgageToLenderTestFigures(evenBetterMortgage, false)

    evenBetterMortgage.calculatorFigures = {
        verified,
        unverified
    }
    evenBetterMortgage.flowHistory = flowRite(evenBetterMortgage, allRates)



    evenBetterMortgage.productsForMortgage  = ()=>getProductsForMortgage(evenBetterMortgage)
    evenBetterMortgage.accountSummary = () => bankStatementSummary(evenBetterMortgage)
    evenBetterMortgage.getUploadSections = ()=>{

    }
    evenBetterMortgage.currentLenderTitle = () => {
        switch(evenBetterMortgage.activeSubmission.lenderCode){
            case 'boi':
                return 'Bank of Ireland'
            case 'haven':
                return 'Haven'
            case 'ics':
                return 'ICS'
            case 'nua':
                return 'NUA'
            case 'ptsb':
                return 'PTSB'
            case 'avant':
                return 'Avant'
            default:
                return null
        }
    }
    return useMemo(()=>{
        if (!mortgage) return null
        return evenBetterMortgage
    }, [evenBetterMortgage])



}

export default useMortgage;